/**
 * RefinoSplit — tela de refino em split-screen, em 100% da largura da janela:
 *   • metade ESQUERDA → referência: Mapa do caso OU Blocos JSON (abas)
 *   • metade DIREITA  → terminal do Claude (TerminalRefinar embedded)
 *
 * ⚠️ É renderizado via PORTAL (createPortal → document.body) pelo ModalDetalhe,
 * porque o drawer do modal é animado com `transform` (framer-motion) e um filho
 * `position: fixed` se ancoraria ao drawer (meia tela), não à viewport.
 *
 * LIVE-REFRESH: o pane de Blocos lê o arquivo VIVO do workdir
 * (RASCUNHO_BLOCOS.json que o Claude edita) com polling — quando o Claude altera
 * um bloco, ele aparece refinado na hora e PISCA em âmbar (bloco-flash).
 */
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { apiBase } from '../api';
import { useArquivosPrazo, useBlocosConteudo, useBlocosWorkdir, useMapaConteudo } from '../hooks';
import { BlocosLista, MapaMarkdown } from './pecaRender';
import { ChatRefinar } from './ChatRefinar';
import { FalcaoPanel } from './FalcaoPanel';

interface Props {
  idPrazo: number;
  numeroProcesso?: string;
  onClose: () => void;
  onFinalizado?: () => void;
}

export function RefinoSplit({ idPrazo, numeroProcesso, onClose, onFinalizado }: Props) {
  const [aba, setAba] = useState<'mapa' | 'blocos'>('blocos');
  const mapa = useMapaConteudo(idPrazo);
  const blocosDrive = useBlocosConteudo(idPrazo);       // último salvo (Drive)
  const blocosLive = useBlocosWorkdir(idPrazo, true);   // vivo (workdir) + polling 1,5s
  const arquivos = useArquivosPrazo(idPrazo);
  const temPdf = (arquivos.data ?? []).some((a) => a.nome.toLowerCase().endsWith('.pdf'));
  const pdfSrc = `${apiBase()}/api/prazos/${idPrazo}/pdf-processo`;

  // Fonte preferencial = workdir (vivo); fallback = Drive (último salvo).
  const blocosArr: any[] = (blocosLive.data?.blocos ?? blocosDrive.data?.blocos ?? []) as any[];
  const aoVivo = !!blocosLive.data;
  const versao = blocosDrive.data?.versao;

  const temBlocos = blocosArr.length > 0;
  const carregando =
    aba === 'mapa' ? mapa.isLoading : !temBlocos && (blocosDrive.isLoading || blocosLive.isLoading);
  const erro =
    aba === 'mapa'
      ? mapa.error
      : !temBlocos && !blocosDrive.isLoading && !blocosLive.isLoading
        ? blocosDrive.error ?? blocosLive.error
        : null;

  // ── Detecção de mudança: pisca os blocos que o Claude alterou ───────────────
  const [destacar, setDestacar] = useState<Set<number>>(new Set());
  const prevRef = useRef<string[] | null>(null);
  const mtimeRef = useRef<number | undefined>(undefined);
  const timerRef = useRef<number | undefined>(undefined);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const live = blocosLive.data;
    if (!live || live.mtime === mtimeRef.current) return; // só quando o arquivo muda
    const atual = (live.blocos as any[]).map((b) => JSON.stringify(b));
    const prev = prevRef.current;
    if (prev) {
      const mudou = new Set<number>();
      const n = Math.max(atual.length, prev.length);
      for (let i = 0; i < n; i++) if (atual[i] !== prev[i]) mudou.add(i);
      if (mudou.size) {
        setDestacar(mudou);
        window.clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(() => setDestacar(new Set()), 2600);
        // leva o primeiro bloco alterado para a vista (escopo: o pane de blocos)
        const first = Math.min(...mudou);
        requestAnimationFrame(() => {
          scrollRef.current
            ?.querySelector(`[data-bloco-idx="${first}"]`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      }
    }
    prevRef.current = atual;
    mtimeRef.current = live.mtime;
  }, [blocosLive.data]);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  function tab(key: 'mapa' | 'blocos', icone: string, rotulo: string) {
    const ativo = aba === key;
    return (
      <button
        onClick={() => setAba(key)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
          ativo
            ? 'bg-blue-500/15 border-blue-500/40 text-blue-700 dark:text-blue-300'
            : 'bg-transparent border-transparent text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
        }`}
      >
        <span>{icone}</span>
        {rotulo}
      </button>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex flex-col md:flex-row bg-slate-950/40 backdrop-blur-sm"
    >
      {/* ─── ESQUERDA: referência (mapa / blocos) ─────────────────────────── */}
      <motion.div
        initial={{ x: -24, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ type: 'spring', damping: 26, stiffness: 220 }}
        className="w-full md:w-1/2 h-1/2 md:h-full flex flex-col bg-white dark:bg-slate-900 border-b md:border-b-0 md:border-r border-slate-200 dark:border-slate-800 shadow-2xl"
      >
        <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-950/40">
          <div className="flex items-center gap-1.5">
            {tab('mapa', '🗺️', 'Mapa do caso')}
            {tab('blocos', '📝', 'Rascunho')}
            {/* PDF do processo abre em NOVA GUIA (não é aba embutida) */}
            {temPdf ? (
              <a
                href={pdfSrc}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-transparent text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                title="Abrir o PDF do processo em nova guia"
              >
                <span>📄</span>
                PDF do processo
                <span aria-hidden className="text-[10px] opacity-70">↗</span>
              </a>
            ) : (
              <span
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-transparent text-slate-300 dark:text-slate-600 cursor-not-allowed"
                title={arquivos.isLoading ? 'Carregando arquivos…' : 'Nenhum PDF do processo encontrado para este prazo'}
              >
                <span>📄</span>
                PDF do processo
              </span>
            )}
            {aba === 'blocos' && versao != null && (
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-600 dark:text-violet-400">
                v{versao}
              </span>
            )}
            {aba === 'blocos' && aoVivo && (
              <span
                className="flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                title="Refletindo as edições do Claude em tempo real (workdir)"
              >
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-60 animate-ping" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
                </span>
                ao vivo
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 text-xl leading-none p-1"
            title="Fechar refino"
            aria-label="Fechar"
          >
            &times;
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 sm:p-6 bg-white/40 dark:bg-slate-950/20">
          {carregando && (
            <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-2">
              <span className="animate-spin text-2xl">⏳</span>
              <span className="font-mono text-xs">Carregando {aba === 'mapa' ? 'mapa' : 'rascunho'}…</span>
            </div>
          )}
          {erro && !carregando && (
            <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-2 text-center px-4">
              <span className="text-2xl">📭</span>
              <span className="text-xs">
                {aba === 'mapa' ? 'Mapa' : 'Rascunho'} ainda não gerado(s). Gere pelo botão
                “Mapa do processo” antes de refinar.
              </span>
            </div>
          )}
          {!carregando && !erro && aba === 'mapa' && <MapaMarkdown conteudo={mapa.data?.conteudo ?? ''} />}
          {!carregando && !erro && aba === 'blocos' && <BlocosLista blocos={blocosArr} destacar={destacar} />}
        </div>
      </motion.div>

      {/* ─── DIREITA: terminal (50% superior) + Falcão (50% inferior) ─────── */}
      <motion.div
        initial={{ x: 24, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ type: 'spring', damping: 26, stiffness: 220 }}
        className="w-full md:w-1/2 h-1/2 md:h-full flex flex-col min-h-0"
      >
        <div className="basis-1/2 min-h-0 overflow-hidden border-b border-slate-200 dark:border-slate-800">
          <ChatRefinar
            embedded
            idPrazo={idPrazo}
            numeroProcesso={numeroProcesso}
            onClose={onClose}
            onFinalizado={onFinalizado}
          />
        </div>
        <div className="basis-1/2 min-h-0 overflow-hidden">
          <FalcaoPanel idPrazo={idPrazo} />
        </div>
      </motion.div>
    </motion.div>
  );
}
